import { useCallback, useEffect, useState } from "react";
import {
  Settings, ToggleLeft, ToggleRight, Save, BarChart3, Loader2,
  CheckCircle2, AlertTriangle,
} from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { api, formatApiError } from "../services/api";

const EDITABLE_TEXT_FIELDS = [
  { key: "name",         label: "Nom de la formule" },
  { key: "tagline",      label: "Sous-titre" },
  { key: "audience",     label: "Public cible" },
  { key: "price_label",  label: "Étiquette prix (affichage)" },
  { key: "cta_label",    label: "Libellé du bouton" },
  { key: "cta_href",     label: "Cible du bouton" },
];


export default function AdminSubscriptions() {
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [toast, setToast] = useState(null);   // {kind: 'ok'|'err', text}
  const [drafts, setDrafts] = useState({});   // per-plan drafts

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, sRes] = await Promise.all([
        api.get("/admin/subscriptions/plans"),
        api.get("/admin/subscriptions/stats"),
      ]);
      const p = pRes.data;
      const s = sRes.data;
      setPlans(p.items || []);
      setStats(s);
      const d = {};
      (p.items || []).forEach((pl) => { d[pl.id] = { ...pl, features_text: (pl.features || []).join("\n") }; });
      setDrafts(d);
    } catch (e) {
      console.warn("[admin-subs] load failed:", formatApiError(e));
      setToast({ kind: "err", text: "Chargement impossible pour le moment." });
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchDraft = (planId, updates) => {
    setDrafts((d) => ({ ...d, [planId]: { ...d[planId], ...updates } }));
  };

  const save = async (planId) => {
    const draft = drafts[planId];
    if (!draft) return;
    setSaving(planId);
    const body = {};
    EDITABLE_TEXT_FIELDS.forEach(({ key }) => { if (draft[key] !== undefined) body[key] = draft[key]; });
    body.orchestrator_monthly_limit = Number(draft.orchestrator_monthly_limit || 0);
    body.coming_soon = !!draft.coming_soon;
    body.highlight = !!draft.highlight;
    body.features = (draft.features_text || "").split(/\n+/).map((s) => s.trim()).filter(Boolean);
    try {
      await api.patch(`/admin/subscriptions/plans/${planId}`, body);
      setToast({ kind: "ok", text: `Formule ${planId} mise à jour.` });
      await load();
    } catch (e) {
      console.warn("[admin-subs] save failed:", formatApiError(e));
      setToast({ kind: "err", text: "Enregistrement impossible pour le moment." });
    }
    finally {
      setSaving(null);
      setTimeout(() => setToast(null), 3200);
    }
  };

  const toggle = async (planId) => {
    setSaving(planId);
    try {
      await api.post(`/admin/subscriptions/plans/${planId}/toggle`);
      await load();
    } catch (e) {
      console.warn("[admin-subs] toggle failed:", formatApiError(e));
      setToast({ kind: "err", text: "Bascule impossible." });
    }
    finally { setSaving(null); }
  };

  return (
    <PrivateLayout>
      <div className="afr-orchestrator" data-testid="admin-subs-root">
        <header className="afr-orchestrator__head">
          <div>
            <span className="afr-eyebrow" style={{ color: "var(--afr-primary)" }}>Administration</span>
            <h1 style={{ fontFamily: "'Outfit',sans-serif", margin: "0.3rem 0 0.4rem", fontSize: "1.9rem" }}>
              Gestion des abonnements
            </h1>
            <p className="afr-muted" style={{ margin: 0, maxWidth: 720 }}>
              Créez ou modifiez les formules, ajustez les avantages, définissez les limites mensuelles
              d'AFRICTAL Orchestrateur et suivez les statistiques d'usage.
            </p>
          </div>
          {stats && (
            <div className="afr-flex-row" style={{ gap: "0.6rem" }} data-testid="admin-subs-kpi">
              <div className="afr-orchestrator__count"><BarChart3 size={20} /><div><strong>{stats.total_users}</strong><span> utilisateurs</span></div></div>
              <div className="afr-orchestrator__count"><Settings size={20} /><div><strong>{stats.orchestrator.total_uses}</strong><span> usages ce mois</span></div></div>
            </div>
          )}
        </header>

        {toast && (
          <div className={`afr-callout afr-callout--${toast.kind === "ok" ? "ok" : "warn"}`} data-testid="admin-subs-toast">
            {toast.kind === "ok" ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />} {toast.text}
          </div>
        )}

        {loading ? (
          <div className="afr-orchestrator__loading"><Loader2 size={22} className="afr-spin" /> Chargement…</div>
        ) : (
          <div className="afr-flex-col" style={{ display: "flex", flexDirection: "column", gap: "1rem" }} data-testid="admin-subs-list">
            {plans.map((p) => {
              const draft = drafts[p.id] || {};
              return (
                <article key={p.id} className="afr-card" data-testid={`admin-sub-card-${p.id}`}>
                  <div className="afr-flex-row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: "0.6rem", marginBottom: "0.6rem" }}>
                    <div>
                      <h3 style={{ fontFamily: "'Outfit',sans-serif", margin: 0 }}>{p.name}</h3>
                      <span className="afr-muted" style={{ fontSize: "0.86rem" }}>{p.audience}</span>
                    </div>
                    <div className="afr-flex-row" style={{ gap: "0.4rem" }}>
                      <span className="afr-badge" style={{ background: p.is_active ? "color-mix(in oklab, var(--afr-primary) 12%, var(--afr-surface))" : "color-mix(in oklab, #d64545 12%, var(--afr-surface))", color: p.is_active ? "var(--afr-primary)" : "#a02020" }}>
                        {p.is_active ? "Actif" : "Désactivé"}
                      </span>
                      <button type="button" className="afr-btn afr-btn--outline afr-btn--sm" onClick={() => toggle(p.id)} disabled={saving === p.id} data-testid={`admin-sub-toggle-${p.id}`}>
                        {p.is_active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                        {p.is_active ? "Désactiver" : "Activer"}
                      </button>
                    </div>
                  </div>

                  <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.8rem" }}>
                    {EDITABLE_TEXT_FIELDS.map(({ key, label }) => (
                      <label key={key} className="afr-field">
                        <span>{label}</span>
                        <input
                          value={draft[key] ?? ""}
                          onChange={(e) => patchDraft(p.id, { [key]: e.target.value })}
                          data-testid={`admin-sub-input-${p.id}-${key}`}
                          maxLength={200}
                        />
                      </label>
                    ))}
                    <label className="afr-field">
                      <span>Limite mensuelle Orchestrateur</span>
                      <input
                        type="number" min="0" max="100000"
                        value={draft.orchestrator_monthly_limit ?? 0}
                        onChange={(e) => patchDraft(p.id, { orchestrator_monthly_limit: e.target.value })}
                        data-testid={`admin-sub-input-${p.id}-limit`}
                      />
                    </label>
                    <label className="afr-field">
                      <span>« Bientôt disponible »</span>
                      <select
                        value={draft.coming_soon ? "1" : "0"}
                        onChange={(e) => patchDraft(p.id, { coming_soon: e.target.value === "1" })}
                        data-testid={`admin-sub-input-${p.id}-soon`}
                      >
                        <option value="0">Non</option>
                        <option value="1">Oui</option>
                      </select>
                    </label>
                    <label className="afr-field">
                      <span>Mise en avant</span>
                      <select
                        value={draft.highlight ? "1" : "0"}
                        onChange={(e) => patchDraft(p.id, { highlight: e.target.value === "1" })}
                        data-testid={`admin-sub-input-${p.id}-highlight`}
                      >
                        <option value="0">Non</option>
                        <option value="1">Oui</option>
                      </select>
                    </label>
                  </div>

                  <label className="afr-field">
                    <span>Avantages (un par ligne)</span>
                    <textarea
                      rows={5}
                      value={draft.features_text ?? ""}
                      onChange={(e) => patchDraft(p.id, { features_text: e.target.value })}
                      data-testid={`admin-sub-input-${p.id}-features`}
                    />
                  </label>

                  {stats && (
                    <div className="afr-muted" style={{ fontSize: "0.86rem", marginTop: "0.4rem" }}>
                      Abonnés : <strong style={{ color: "var(--afr-secondary)" }}>{(stats.plans.find((x) => x.id === p.id) || {}).subscribers ?? 0}</strong>
                    </div>
                  )}

                  <div className="afr-flex-row" style={{ justifyContent: "flex-end", marginTop: "0.6rem" }}>
                    <button
                      type="button"
                      className="afr-btn afr-btn--primary afr-btn--sm"
                      onClick={() => save(p.id)}
                      disabled={saving === p.id}
                      data-testid={`admin-sub-save-${p.id}`}
                    >
                      {saving === p.id ? <><Loader2 size={13} className="afr-spin" /> Enregistrement…</> : <><Save size={13} /> Enregistrer</>}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </PrivateLayout>
  );
}
